// src/pages/LogExercise.jsx
/* LogExercise page */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import ExerciseFlowSteps from "../components/ExerciseFlowSteps";
import SeoHead from "../components/SeoHead";
import { getSetsByExercise, getPastSetsByExercise, updateSetsByExercise } from "../services/exerciseService";
import "./LogExercise.css";
import "../styles/Common.css";
import { pullAndMergeExerciseLog, pushExerciseLog } from '../services/syncService';
import { todayYMDLocal } from "../utils/dateUtils";
import { useAuthCompat } from "../utils/clerkConfig";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseNameById } from "../utils/exerciseName";
import { getExerciseDetails } from "../services/apiClient";
import { trackEvent } from "../utils/analytics";

export default function LogExercise() {
    const { t, localizePath } = useI18n();
    const { exerciseId, date } = useParams();
    const navigate = useNavigate();
    const today = todayYMDLocal();
    const { getToken } = useAuthCompat();
    const { language } = useI18n();

    // Initialize sets state with an empty array
    const [sets, setSets] = useState([]);
    const [pastLogs, setPastLogs] = useState([]);
    const [saveError, setSaveError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(date || today);
    const [originalDate, setOriginalDate] = useState(date || today);
    const [hasDetails, setHasDetails] = useState(false);

    useEffect(() => {
        let mounted = true;
        async function checkDetails() {
            try {
                const data = await getExerciseDetails(getToken, exerciseId, language);
                if (mounted && data && Object.keys(data).length > 0) {
                    setHasDetails(true);
                } else if (mounted) {
                    setHasDetails(false);
                }
            } catch (err) {
                if (mounted) {
                    setHasDetails(false);
                }
            }
        }
        checkDetails();
        return () => { mounted = false; };
    }, [exerciseId, language, getToken]);

    const autoSaveTimeoutRef = useRef(null);
    const hasInitializedAutoSaveRef = useRef(false);
    const latestMetaRef = useRef({
        selectedDate,
        originalDate,
        exerciseId,
    });


    // Function to initialize sets with data from localStorage
    const initializeSets = (date, exId) => {
        const initialSets = getSetsByExercise(date, exId);
        return initialSets.length > 0
            ? [...initialSets, { weight: "", reps: "" }]
            : [{ weight: "", reps: "" }];
    };

    // Build list of recent logs (declared early to satisfy lint rules)
    const fetchPastLogs = useCallback(() => {
        if (!selectedDate) {
            setPastLogs([]);
            return;
        }

        setPastLogs(getPastSetsByExercise(exerciseId, selectedDate, 10));
    }, [selectedDate, exerciseId]);

    // Initialize sets on component mount and when exerciseId changes
    // We don't include selectedDate in the dependency array to prevent reloading sets when date changes
    useEffect(() => {
        // Only initialize sets from storage when component mounts or exerciseId changes
        // This prevents losing sets data when only the date changes
        setSets(initializeSets(originalDate, exerciseId));
        fetchPastLogs();
    }, [exerciseId, originalDate, fetchPastLogs]);
    
    // Separate effect for fetching past logs when selectedDate changes
    useEffect(() => {
        fetchPastLogs();
    }, [selectedDate, fetchPastLogs]);

    useEffect(() => {
        latestMetaRef.current = {
            selectedDate,
            originalDate,
            exerciseId,
        };
    }, [selectedDate, originalDate, exerciseId]);

    

    const isSetSavable = (set) => {
        const reps = parseInt(set.reps, 10);
        const hasValidReps = set.reps !== "" && !Number.isNaN(reps) && reps > 0;

        // Weight is considered valid if it's a non-negative number OR empty (treated as 0)
        const hasValidWeightNumber = set.weight !== "" && !Number.isNaN(parseFloat(set.weight)) && parseFloat(set.weight) >= 0;
        const isEmptyWeight = set.weight === "";

        return hasValidReps && (hasValidWeightNumber || isEmptyWeight);
    };

    const normalizeSetsForSave = (rawSets) =>
        rawSets.map((s) => {
            const reps = parseInt(s.reps, 10);
            const hasValidReps = s.reps !== "" && !Number.isNaN(reps) && reps > 0;
            // If reps are valid and weight is empty, coerce weight to "0" so it persists
            if (hasValidReps && (s.weight === "" || Number.isNaN(parseFloat(s.weight)))) {
                return { ...s, weight: "0" };
            }
            return s;
        });

    useEffect(() => {
        if (!hasInitializedAutoSaveRef.current) {
            hasInitializedAutoSaveRef.current = true;
            return;
        }

        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        autoSaveTimeoutRef.current = setTimeout(async () => {
            const { selectedDate: currentDate, originalDate: currentOriginalDate, exerciseId: currentExerciseId } = latestMetaRef.current;

            if (!currentDate) {
                return;
            }

            const normalized = normalizeSetsForSave(sets);
            // Pull remote first and merge to include changes from other devices
            try { await pullAndMergeExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }

            const saveResult = updateSetsByExercise(
                currentDate,
                currentExerciseId,
                normalized,
                currentDate !== currentOriginalDate ? currentOriginalDate : null
            );

            if (!saveResult) {
                setSaveError(t("log.autosaveFailed"));
                return;
            }

            // Push the full log to API in the background (best-effort)
            try { await pushExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }

            setSaveError(null);

            if (currentDate !== currentOriginalDate) {
                setOriginalDate(currentDate);
            }
        }, 500);

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [getToken, sets, t]);

    const handleChange = (e, index) => {
        const { name, value } = e.target;

        const newSets = [...sets];
        newSets[index] = { ...newSets[index], [name]: value };

        const isLastSet = index === newSets.length - 1;
        const weight = parseFloat(newSets[index].weight);
        const reps = parseInt(newSets[index].reps, 10);
        const isSetComplete =
            newSets[index].weight !== "" && !Number.isNaN(weight) && weight >= 0 &&
            newSets[index].reps !== "" && !Number.isNaN(reps) && reps > 0;

        if (isLastSet && isSetComplete) {
            setSets([...newSets, { weight: "", reps: "" }]);
        } else {
            setSets(newSets);
        }
    };

    const handleDelete = (index) => {
        const newSets = sets.filter((_, i) => i !== index);

        if (newSets.length === 0 || newSets.every((set) => set.weight && set.reps)) {
            newSets.push({ weight: "", reps: "" });
        }

        setSets(newSets);
    };

    // Compute the last filled (savable) row index to place the Clone button there
    const lastSavableIndex = (() => {
        let idx = -1;
        for (let i = 0; i < sets.length; i++) {
            if (isSetSavable(sets[i])) idx = i;
        }
        return idx;
    })();

    const handleCopyLast = () => {
        setSets((prev) => {
            if (!prev || prev.length === 0) return prev;
            const lastIndex = prev.length - 1; // expected empty row
            const srcIndex = lastIndex - 1;
            if (srcIndex < 0) return prev;

            const source = prev[srcIndex];
            // Only copy if the previous row is savable (complete)
            const reps = parseInt(source?.reps, 10);
            const hasValidReps = source?.reps !== "" && !Number.isNaN(reps) && reps > 0;
            const hasValidWeightNumber = source?.weight !== "" && !Number.isNaN(parseFloat(source?.weight)) && parseFloat(source?.weight) >= 0;
            const isEmptyWeight = source?.weight === ""; // empty weight allowed, saved as 0 later
            const canCopy = hasValidReps && (hasValidWeightNumber || isEmptyWeight);
            if (!canCopy) return prev;

            const copied = { weight: String(source.weight ?? ""), reps: String(source.reps ?? "") };
            const next = [...prev];
            // Fill the last (empty) row with copied values, then add a new empty row
            next[lastIndex] = copied;
            next.push({ weight: "", reps: "" });
            return next;
        });
    };

    // Note: +/- adjust buttons removed for layout stability across devices.

    const saveSets = async () => {
        try {
            // Clear any previous error
            setSaveError(null);
            
            // Validate date format (YYYY-MM-DD)
            const datePattern = /^\d{4}-\d{2}-\d{2}$/;
            if (!datePattern.test(selectedDate)) {
                setSaveError(t("log.invalidDate"));
                return;
            }
            
            const normalized = normalizeSetsForSave(sets);
            const completeSets = normalized.filter(isSetSavable);

            // Check if the date has changed
            const dateHasChanged = selectedDate !== originalDate;
            
            // If the date has changed, pass the original date to updateSetsByExercise
            // This will remove the exercise from the original date's log
            // If Clerk is enabled, pull remote first and merge into local to include other devices
            try { await pullAndMergeExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }

            const saveResult = updateSetsByExercise(
                selectedDate, 
                exerciseId, 
                completeSets,
                dateHasChanged ? originalDate : null
            );
            
            if (saveResult) {
                trackEvent("log_exercise", {
                    exercise_id: exerciseId,
                    sets_count: completeSets.length,
                });
                // After local save, push the full log to API if available
                try { await pushExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }
                navigate(localizePath("/"));
            } else {
                setSaveError(t("log.saveFailed"));
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Save sets failed:", error);
            setSaveError(t("log.saveFailed"));
        }
    };

    // Function to get the formatted exercise name for UI (Title Case)
    const formatExerciseName = (id) => {
        return id
            .replace(/_/g, " ")
            .split(" ")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };
    const formattedExerciseName = getLocalizedExerciseNameById(
        exerciseId,
        t,
        formatExerciseName(exerciseId)
    );

    return (
        <div className="logExercisePage pageContainer">
            <SeoHead
                title={t("log.seoTitle", { exercise: formattedExerciseName })}
                description={t("log.seoDescription", { exercise: formattedExerciseName })}
                canonicalPath={localizePath(`/LogExercise/${exerciseId}`)}
                noIndex={true}
            />
            <Breadcrumbs current={t("breadcrumbs.logExerciseSection")} />
            <Link to={localizePath("/")} className="flowBackLink appTextLink">
                <span className="flowBackLinkIcon" aria-hidden="true">←</span>
                <span className="flowBackLinkLabel">{t("common.back")}</span>
            </Link>
            <div className="logExerciseIntro">
                <ExerciseFlowSteps activeStep={2} />
                <h1 className="sectionHeading">
                    {hasDetails ? (
                        <Link to={localizePath(`/exercises/${exerciseId}`)} className="appTextLink" style={{ textDecoration: 'underline' }}>
                            {t("log.title", { exercise: formattedExerciseName })}
                        </Link>
                    ) : (
                        t("log.title", { exercise: formattedExerciseName })
                    )}
                </h1>
            </div>

            <div className="formContainer logExerciseCard">
                {sets.length === 0 ? (
                    <p>{t("log.noSets")}</p>
                ) : (
                    <div className="setsTableWrap">
                        <table className="setsTable">
                            <thead>
                            <tr>
                                <th>{t("log.set")}</th>
                                <th>{t("log.weight")}</th>
                                <th title={t("log.repsRequiredTitle")}>
                                    {t("log.reps")}{" "}
                                    <span className="repsRequiredMark" aria-hidden="true">*</span>
                                </th>
                                <th>{t("log.actions")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sets.map((setItem, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <input
                                            type="number"
                                            name="weight"
                                            placeholder={t("log.weight")}
                                            value={setItem.weight}
                                            onChange={(e) => handleChange(e, index)}
                                            className="input"
                                            min="0"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="reps"
                                            placeholder={t("log.reps")}
                                            value={setItem.reps}
                                            onChange={(e) => handleChange(e, index)}
                                            className="input"
                                            min="0"
                                        />
                                    </td>
                                    <td>
                                        <div className="actionsCell">
                                            {isSetSavable(setItem) && (
                                                <button
                                                    className="deleteButton appButton appButton--danger"
                                                    aria-label={t("log.deleteAria")}
                                                    title={t("log.delete")}
                                                    onClick={() => handleDelete(index)}
                                                >
                                                    <span aria-hidden="true">×</span>
                                                </button>
                                            )}
                                            {index === lastSavableIndex && (
                                                <button
                                                    type="button"
                                                    className="appButton cloneButton"
                                                    aria-label={t("log.cloneAria")}
                                                    title={t("log.clone")}
                                                    onClick={handleCopyLast}
                                                >
                                                    <span aria-hidden="true">⧉</span>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                {saveError && (
                    <div className="errorMessage">
                        <strong>{t("log.errorPrefix")}</strong> {saveError}
                    </div>
                )}
                
                <div className="formFooter">
                    <div className="dateInputContainer">
                        <label htmlFor="exerciseDate" className="dateInputLabel">{t("log.dateLabel")}</label>
                        <input
                            id="exerciseDate"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="dateInput"
                            pattern="\d{4}-\d{2}-\d{2}"
                            required
                        />
                    </div>
                    
                    <button type="button" onClick={saveSets} className="saveButton appButton">
                        {t("log.save")}
                    </button>
                </div>
            </div>

            {/* Table with the last 10 exercise sessions */}
            {pastLogs.length > 0 && (
                <div className="pastLogsContainer logExerciseCard">
                    <h2 className="sectionHeading">{t("log.lastSessions", { count: pastLogs.length })}</h2>
                    <div className="pastLogsTableWrap">
                        <table className="pastLogsTable">
                            <thead>
                            <tr>
                                <th>{t("log.date")}</th>
                                <th>{t("log.weight")}</th>
                                <th>{t("log.reps")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {pastLogs.map((log, index) => (
                                <tr key={index}>
                                    <td>{log.date}</td>
                                    <td>{log.weight}</td>
                                    <td>{log.reps}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
